// Shared GLSL used by the sky, terrain, grass and patched standard materials.
// All colors are linear HDR; tone mapping happens in the post pipeline.

export const COMMON_UNIFORMS = /* glsl */ `
uniform float uTime;
uniform vec3 uSunDir;
uniform vec3 uSunColor;
uniform vec3 uSkyZenith;
uniform vec3 uSkyHorizon;
uniform vec3 uSkyHorizonAway;
uniform vec3 uHazeColor;
uniform vec3 uSkyAmbient;
uniform vec3 uGroundAmbient;
uniform vec3 uTransTint;
uniform float uFogDensity;
uniform float uFogFalloff;
uniform sampler2D uNoise;
uniform vec2 uWindDir;
uniform float uWindStrength;
uniform vec2 uGustOffsetA;
uniform vec2 uGustOffsetB;
uniform vec2 uCloudOffset;
uniform float uCloudCoverage;
uniform float uCloudShadow;
uniform vec4 uCapsuleA;
uniform vec4 uCapsuleB;
uniform vec4 uHatSphere;
`;

export const NOISE_FNS = /* glsl */ `
vec4 hash42(vec2 p) {
  vec4 p4 = fract(vec4(p.xyxy) * vec4(0.1031, 0.1030, 0.0973, 0.1099));
  p4 += dot(p4, p4.wzxy + 33.33);
  return fract((p4.xxyz + p4.yzzw) * p4.zywx);
}
float hash12(vec2 p) {
  vec3 p3 = fract(vec3(p.xyx) * 0.1031);
  p3 += dot(p3, p3.yzx + 33.33);
  return fract((p3.x + p3.y) * p3.z);
}
`;

export const WIND_FNS = /* glsl */ `
// Mirrored on the CPU in Wind.gustAt so audio and cloth feel the same gusts.
float gustAt(vec2 xz) {
  float a = texture2D(uNoise, (xz - uGustOffsetA) * (1.0 / 96.0)).r;
  float b = texture2D(uNoise, (xz - uGustOffsetB) * (1.0 / 41.0)).a;
  return smoothstep(0.34, 0.8, a * 0.65 + b * 0.35);
}
`;

export const SKY_FNS = /* glsl */ `
vec3 skyBase(vec3 dir) {
  float y = dir.y;
  float yh = max(y, 0.0);
  vec2 flatDir = normalize(dir.xz + vec2(1e-4, 0.0));
  vec2 flatSun = normalize(uSunDir.xz + vec2(1e-4, 0.0));
  float muH = dot(flatDir, flatSun);
  vec3 horizon = mix(uSkyHorizonAway, uSkyHorizon, smoothstep(-0.7, 0.85, muH));
  vec3 col = mix(horizon, uSkyZenith, pow(yh, 0.42));
  col = mix(col, uHazeColor, smoothstep(0.0, -0.25, y));
  float mu = clamp(dot(dir, uSunDir), -1.0, 1.0);
  float horizonW = exp(-yh * 4.0);
  float g1 = 0.8;
  float hg1 = (1.0 - g1 * g1) / pow(max(1.0 + g1 * g1 - 2.0 * g1 * mu, 1e-4), 1.5);
  float g2 = 0.35;
  float hg2 = (1.0 - g2 * g2) / pow(max(1.0 + g2 * g2 - 2.0 * g2 * mu, 1e-4), 1.5);
  col += uSunColor * (hg1 * 0.0048 * (0.45 + 0.9 * horizonW) + hg2 * 0.04 * horizonW);
  return col;
}

vec3 fogColorFor(vec3 rd) {
  return skyBase(normalize(vec3(rd.x, max(rd.y, 0.0) * 0.3, rd.z)));
}

vec3 applyAtmosphere(vec3 col, vec3 wpos) {
  vec3 ro = cameraPosition;
  vec3 d = wpos - ro;
  float dist = length(d);
  vec3 rd = d / max(dist, 1e-3);
  float b = uFogFalloff;
  float k = rd.y * b;
  float integral = abs(k) < 1e-5 ? dist : (1.0 - exp(-dist * k)) / k;
  float fog = uFogDensity * exp(-max(ro.y, -40.0) * b) * integral;
  float amt = 1.0 - exp(-fog);
  vec3 fc = fogColorFor(rd);
  float sun = pow(max(dot(rd, uSunDir), 0.0), 5.0);
  fc += uSunColor * sun * 0.06;
  return mix(col, fc, amt);
}
`;

export const LIGHT_FNS = /* glsl */ `
float cloudShadowAt(vec3 wp) {
  vec2 p = wp.xz + uSunDir.xz * ((700.0 - wp.y) / max(uSunDir.y, 0.08));
  float n = texture2D(uNoise, (p + uCloudOffset) * (1.0 / 2100.0)).r * 0.6
          + texture2D(uNoise, (p + uCloudOffset * 1.2) * (1.0 / 760.0)).g * 0.4;
  float c = smoothstep(uCloudCoverage - 0.04, uCloudCoverage + 0.22, n);
  return 1.0 - c * uCloudShadow;
}

// Analytic soft shadow of the player: body capsule + hat disc, cast along the sun ray.
float capsuleOcclusion(vec3 p, vec3 a, vec3 b, float r) {
  vec3 L = uSunDir;
  vec3 v = b - a;
  vec3 w0 = p - a;
  float bb = dot(L, v);
  float c = max(dot(v, v), 1e-5);
  float d = dot(L, w0);
  float e = dot(v, w0);
  float den = max(c - bb * bb, 1e-5);
  float tc = clamp((e - bb * d) / den, 0.0, 1.0);
  vec3 q = a + v * tc;
  float s = max(dot(q - p, L), 0.0);
  float dist = length(p + L * s - q);
  float soft = r + s * 0.035;
  float occ = 1.0 - smoothstep(r * 0.35, soft, dist);
  return occ * step(0.02, s);
}

float characterShadow(vec3 p) {
  float strength = uCapsuleB.w;
  if (strength <= 0.0) return 1.0;
  float body = capsuleOcclusion(p, uCapsuleA.xyz, uCapsuleB.xyz, uCapsuleA.w);
  float hat = capsuleOcclusion(p, uHatSphere.xyz - vec3(0.0, 0.02, 0.0), uHatSphere.xyz + vec3(0.0, 0.02, 0.0), uHatSphere.w);
  return 1.0 - max(body, hat) * strength;
}

// Thin foliage lighting: wrapped diffuse, forward scattering through blades, soft sheen.
vec3 foliageLight(vec3 albedo, vec3 N, vec3 V, float ao, float trans, float shadow, float sheen) {
  vec3 L = uSunDir;
  float ndl = dot(N, L);
  float diff = clamp((ndl + 0.4) / 1.4, 0.0, 1.0);
  float vdl = clamp(dot(-V, L), 0.0, 1.0);
  float back = pow(vdl, 3.0) * 0.45 + pow(vdl, 14.0) * 1.1;
  vec3 H = normalize(L + V);
  float spec = pow(clamp(dot(N, H), 0.0, 1.0), 24.0) * sheen;
  vec3 direct = uSunColor * shadow * (albedo * diff * 0.6 + albedo * uTransTint * back * trans + vec3(spec));
  vec3 amb = albedo * mix(uGroundAmbient, uSkyAmbient, clamp(N.y * 0.5 + 0.5, 0.0, 1.0)) * ao;
  return direct + amb;
}
`;

export const TERRAIN_HEIGHT_FNS = /* glsl */ `
uniform sampler2D uHeightTex;
uniform vec3 uHeightInfo; // x = half extent, y = cell size, z = resolution
float terrainHeight(vec2 xz) {
  vec2 g = (xz + uHeightInfo.x) / uHeightInfo.y;
  vec2 gi = floor(g);
  vec2 f = g - gi;
  int res = int(uHeightInfo.z);
  ivec2 i0 = clamp(ivec2(gi), ivec2(0), ivec2(res - 1));
  ivec2 i1 = clamp(ivec2(gi) + 1, ivec2(0), ivec2(res - 1));
  float h00 = texelFetch(uHeightTex, ivec2(i0.x, i0.y), 0).r;
  float h10 = texelFetch(uHeightTex, ivec2(i1.x, i0.y), 0).r;
  float h01 = texelFetch(uHeightTex, ivec2(i0.x, i1.y), 0).r;
  float h11 = texelFetch(uHeightTex, ivec2(i1.x, i1.y), 0).r;
  return mix(mix(h00, h10, f.x), mix(h01, h11, f.x), f.y);
}
`;
